import React, { useEffect } from "react";
import { createPortal } from "react-dom";

interface CalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export const CalculatorModal: React.FC<CalculatorModalProps> = ({
  isOpen,
  onClose,
  children,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.classList.remove("overflow-hidden");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-1000 flex items-start justify-center overflow-y-auto px-5 py-12"
      style={{
        background: "rgba(15, 5, 18, 0.78)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        animation: "calcModalFade 0.28s ease",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative my-auto w-full max-w-235"
        style={{ animation: "calcModalRise 0.32s cubic-bezier(0.16,1,0.3,1)" }}
      >
        <button
          onClick={onClose}
          aria-label="Close calculator"
          className="text-[22px] absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-full leading-none text-white transition-all duration-200 hover:rotate-90"
          style={{
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.25)",
          }}
        >
          &times;
        </button>
        {children}
      </div>

      <style>{`
        @keyframes calcModalFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes calcModalRise {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>,
    document.body
  );
};
